// File: src/features/coach/components/ImageUploadAndCrop.tsx
import React, { useState, useCallback, useEffect } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import Modal from '../../../components/common/Modal';
import Button from '../../../components/common/Button';
import getCroppedImg from '../../../utils/cropImage';
import { UserCircleIcon, PencilIcon } from '@heroicons/react/24/outline';

interface ImageUploadAndCropProps {
  onFileCropped: (file: File) => void;
  currentImageUrl?: string | null;
  isUploading: boolean;
}

const ImageUploadAndCrop: React.FC<ImageUploadAndCropProps> = ({ onFileCropped, currentImageUrl, isUploading }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  useEffect(() => {
    console.log(`[ImageUploadAndCrop] Received image URL prop for display:`, currentImageUrl);
  }, [currentImageUrl]);

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result as string);
        setIsModalOpen(true);
      });
      reader.readAsDataURL(file);
    }
  };

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSave = async () => {
    if (imageSrc && croppedAreaPixels) {
      const croppedFile = await getCroppedImg(imageSrc, croppedAreaPixels, 'profile.jpg');
      if (croppedFile) {
        onFileCropped(croppedFile);
      }
      setIsModalOpen(false);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setImageSrc(null);
  };
  
  // NEW: Error handler for the image tag
  const handleImageError = () => {
    console.error(`[ImageUploadAndCrop] ERROR: Failed to load image. The browser could not load the resource from the provided src: "${currentImageUrl}". Check if the URL is correct and the resource is accessible.`);
  };

  return (
    <div className="flex flex-col items-center space-y-2">
      <div className="relative w-32 h-32">
        {currentImageUrl ? (
          <img 
            src={currentImageUrl} 
            alt="Profile" 
            className="w-32 h-32 rounded-full object-cover"
            onError={handleImageError} // NEW: Added error handler
          />
        ) : (
          <UserCircleIcon className="w-32 h-32 text-neutral-300" />
        )}
        <label
          htmlFor="profile-picture-upload"
          className="absolute -bottom-1 -right-1 bg-white p-2 rounded-full shadow-md cursor-pointer hover:bg-neutral-lightest"
        >
          <PencilIcon className="h-5 w-5 text-primary" />
          <input
            id="profile-picture-upload"
            type="file"
            accept="image/*"
            onChange={onFileChange}
            className="hidden"
          />
        </label>
      </div>

      {isModalOpen && imageSrc && (
        <Modal isOpen={isModalOpen} onClose={handleClose} title="Crop Your Profile Picture" size="md">
          <div className="relative w-full h-64 bg-neutral-darkest">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
          <div className="p-4 space-y-4">
            <div>
              <label htmlFor="zoom" className="block text-sm font-medium text-neutral-dark">Zoom</label>
              <input
                id="zoom"
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer range-lg accent-primary"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button onClick={handleSave} isLoading={isUploading}>Save Picture</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ImageUploadAndCrop;